// ============================================
// Inventory & Equipment — replaces InventoryManager.cs
// Item database, equipment slots, buff calculation,
// set bonuses, wearing flags, ownership sync
// ============================================

import { events, Events } from '../engine/events.js';
import { Stats, EquipSlotNames, MULTI_SLOT_MAP } from '../engine/constants.js';

// Tags considered feminine for EquippedFeminine flag (checked in Upper + Bra slots)
const FEMININE_UPPER_TAGS = new Set([
    'dress', 'sundress', 'crop_top', 'blouse', 'oversized_sweater',
    'schoolgirl_uniform', 'maid_outfit', 'bunny_suit',
    'club_uniform', 'bar_dress', 'cafe_maid_uniform', 'nightie', 'crop_jacket',
    'latex_dress', 'fishnet_bodysuit',
    // Swimwear tops: a boy in a bikini reads feminine to bystanders
    // (visibly_femme + EquippedFeminine narrative gates). The render side is
    // unaffected — presenting_masc was already false in these outfits (no
    // masc garment worn).
    'resort_bikini', 'bikini_loaner',
]);

// Upper-slot items that cover BOTH top and bottom (dresses, leotards,
// full uniforms). Used by _updateDressedFlag so a player in just a dress
// counts as "dressed" even without a Lower item equipped.
const FULL_BODY_UPPER_TAGS = new Set([
    'dress', 'sundress', 'schoolgirl_uniform', 'maid_outfit', 'bunny_suit',
    'bar_dress', 'cafe_maid_uniform', 'nightie', 'latex_dress',
    // Single-garment full-body coverage that fills ONLY the Upper slot (no
    // matching Lower). Must be whitelisted or the is_dressed gate traps the
    // player: full body covered, Lower slot empty → "get dressed" with no fix.
    // fishnet_bodysuit = "neck to ankle" bodystocking. foundby:VPS 2026-06-25.
    'fishnet_bodysuit',
    // Changing-booth loaner bikini: a two-piece SET carried as ONE Upper-slot
    // item (no separate Underwear bottom like the resort set), so the Lower
    // slot is legitimately empty while worn.
    'bikini_loaner',
    // Masc full outfits carried as ONE Upper-slot item: the suit is jacket +
    // trousers ("sharp-looking suit"), workwear is full canvas coveralls +
    // hi-vis. Worn alone they read fully dressed — pre-fix the gate demanded
    // jeans UNDER the suit. foundby:Monki 0.3.0.4 (VPS 2026-07-15).
    'suit', 'workwear',
    // Velvet performer outfit — complete one-piece stage outfit, sibling of
    // the listed bar_dress/cafe_maid_uniform (same sweep as the suit fix).
    'club_uniform',
]);
// Swimwear bottoms that occupy the Underwear slot, not Lower. The resort bikini
// is a matched SET: resort_bikini (Upper) + resort_bikini_bottom (Underwear),
// so the Lower slot is legitimately empty while the player is fully clothed for
// the beach/resort. Counted as a valid "bottom half" by _updateDressedFlag so a
// player who dresses for the beach at home isn't blocked at the leave-apartment
// gate. foundby:VPS 2026-06-25 ("won't let me go outside, clothes are on").
const SWIMWEAR_BOTTOM_TAGS = new Set([
    'resort_bikini_bottom',
]);
const FEMININE_BRA_TAGS = new Set([
    'lingerie_set', 'corset',
]);

// Masculine clothing (top + bottom) that becomes un-equippable once the player
// has feminized past MASC_CLOTHING_FEM_CAP — "you can't go back to boy clothes
// now." Bounds the _masc image axis to low/mid fem (a high-fem body presenting
// masc has no believable render). `suit` is INTENTIONALLY excluded: the male
// office route gates it separately (Office.ink, Fem<=60). Enforced only in the
// wardrobe UI + the wardrobe:equip handler — story-forced equips (#item:equip
// via TAG_ITEM) bypass it.
const MASCULINE_UPPER_TAGS = new Set([
    'tshirt', 'hoodie', 'tank_top', 'vneck_tee', 'football_shirt', 'leather_jacket',
]);
const MASCULINE_LOWER_TAGS = new Set([
    'jeans', 'sweatpants', 'swim_trunks',
]);
// Feminine bottoms (skirts). Counted alongside FEMININE_UPPER/BRA for
// presenting_masc only: a male shirt + a skirt, OR jeans + a crop top, both
// read FEMININE — a feminine garment on EITHER half defeats masc presentation.
// (EquippedFeminine itself stays Upper/Bra-only; its other consumers rely on that.)
const FEMININE_LOWER_TAGS = new Set([
    'miniskirt', 'pleated_skirt', 'skater_skirt', 'pvc_skirt',
]);
const MASC_CLOTHING_FEM_CAP = 50;

// CHASTITY_REMOVAL (0.2.8): cage item tags. When someone else holds the key
// (permanent lock, or a Master keyholder), the wardrobe Front-slot unequip is
// blocked — the cage only comes off through a story beat (Chastity_Devotion,
// Earned Relief, Quiet Exit), never on a wardrobe whim. Independent self-lock
// (no keyholder) stays freely removable. Enforced in the wardrobe UI + the
// wardrobe:unequip / wardrobe:loadPreset handlers — story-forced removals use
// #item:unequip / equipByTag directly and bypass this gate.
// chastity_belt_pink (KITTEN_CHROME §4b) is a Front-slot chastity device too:
// it sets wearing_chastity and must obey the same keyholder/permanent_lock gate
// as the cages. It is NOT a cage_tier (the Master downsize chain stays cage-only).
const CAGE_TAGS = new Set(['chastity_cage', 'pink_cage', 'flat_cage', 'chastity_belt_pink']);
// The Master's downsize cage chain (regular → small → flat). Swapping AMONG
// these while keyholder-locked is sanctioned: the player stays caged, so the
// keyholder lock (which protects against going UNcaged) is not violated. The
// belt (chastity_belt_pink) is deliberately NOT here — it must stay blocked from
// displacing a locked cage (belt-over-cage, deep-review P1-9). Without this
// allowlist the Master-ordered downsize (Bar_Order_Downsize_Small: buy + wear the
// pink cage) softlocks: the locked regular cage can't be displaced at the
// wardrobe, cage_tier stays 1, and Bar_Order_Downsize_Small_Complete (gated
// cage_tier==2) never fires. foundby:VPS player 0.2.8.3 ("Switching to pink cage").
const DOWNSIZE_CAGE_TAGS = new Set(['chastity_cage', 'pink_cage', 'flat_cage']);

// Item-slot tags whose EQUIPPED state actually matters. The vibrator and remote
// egg push `wearing_vib` when equipped (see _updateInkFlags); the four cosmetic
// consumables (lip_gloss/nail_polish/perfume/shaving_kit) carry buffs and are
// caught by buffs.length, so only the worn-flag toys need naming here. Everything
// else in the generic "Item" slot is an OWNED-ONLY item — buying it sets an
// `owns_*` flag that gates a story ACTION (use the dildo, write in the journal,
// listen to the hypno USB, tie the rope), not a worn effect. Equipping those does
// nothing, so the wardrobe suppresses their Equip button (it would only evict a
// real buff item from the shared single Item slot). See isOwnedOnlyItem().
const WORN_ITEM_SLOT_TAGS = new Set(['vibrator', 'remote_vib']);

// Back-slot plugs that carry a VISIBLE tail. Drives the Ink `wearing_tail`
// flag that NPC reaction beats gate on. Add every new tailed plug here rather
// than introducing a parallel flag (feedback_inventory_owns_or_pattern).
const TAIL_PLUG_TAGS = new Set(['tail_plug_kitten', 'tail_plug_puppy']);

// Starter items given at new game
const STARTER_ITEM_TAGS = ['tshirt', 'jeans', 'boxershorts', 'sneakers'];

// Maps item ownership to Ink variables
const OWNERSHIP_VAR_MAP = [
    { tag: 'chastity_cage', inkVar: 'owns_chastity' },
    { tag: 'pink_cage', inkVar: 'owns_chastity' },
    { tag: 'pink_cage', inkVar: 'owns_small_cage' },
    { tag: 'flat_cage', inkVar: 'owns_chastity' },
    { tag: 'flat_cage', inkVar: 'owns_flat_cage' },
    { tag: 'chastity_belt_pink', inkVar: 'owns_chastity' },
    { tag: 'plug', inkVar: 'owns_plug' },
    { tag: 'plug_medium', inkVar: 'owns_plug' },
    { tag: 'plug_large', inkVar: 'owns_plug' },
    { tag: 'dildo', inkVar: 'owns_dildo' },
    { tag: 'dildo_medium', inkVar: 'owns_dildo' },
    { tag: 'dildo_large', inkVar: 'owns_dildo' },
    { tag: 'dildo_medium', inkVar: 'owns_dildo_medium' },
    { tag: 'dildo_large', inkVar: 'owns_dildo_large' },
    { tag: 'plug_medium', inkVar: 'owns_plug_medium' },
    { tag: 'plug_large', inkVar: 'owns_plug_large' },
    { tag: 'vibrator', inkVar: 'owns_vibrator' },
    { tag: 'remote_vib', inkVar: 'owns_remote_vib' },
    { tag: 'hypno_usb', inkVar: 'owns_hypno_usb' },
    { tag: 'sissy_guide', inkVar: 'owns_sissy_guide' },
    { tag: 'club_uniform', inkVar: 'club_uniform_given' },
    { tag: 'panties_black', inkVar: 'has_panties' },
    { tag: 'panties_pink', inkVar: 'has_panties' },
    // Regular mall clothing (0.3.0.7, VPS "Missing Purchases"). HEAL-ONLY:
    // syncFromInk ADDS a dropped garment when its var is true but NEVER
    // reverse-deletes on false, so pre-0.3.0.7 saves (vars default false)
    // keep their wardrobe. Auto-set true on purchase via _syncOwnershipVarsToInk.
    { tag: 'tshirt', inkVar: 'owns_tshirt', healOnly: true },
    { tag: 'jeans', inkVar: 'owns_jeans', healOnly: true },
    { tag: 'boxershorts', inkVar: 'owns_boxershorts', healOnly: true },
    { tag: 'sneakers', inkVar: 'owns_sneakers', healOnly: true },
    { tag: 'hoodie', inkVar: 'owns_hoodie', healOnly: true },
    { tag: 'sweatpants', inkVar: 'owns_sweatpants', healOnly: true },
    { tag: 'tank_top', inkVar: 'owns_tank_top', healOnly: true },
    { tag: 'swim_trunks', inkVar: 'owns_swim_trunks', healOnly: true },
    { tag: 'vneck_tee', inkVar: 'owns_vneck_tee', healOnly: true },
    { tag: 'football_shirt', inkVar: 'owns_football_shirt', healOnly: true },
    { tag: 'suit', inkVar: 'owns_suit', healOnly: true },
    { tag: 'workwear', inkVar: 'owns_workwear', healOnly: true },
    { tag: 'leather_jacket', inkVar: 'owns_leather_jacket', healOnly: true },
    { tag: 'leather_shoes', inkVar: 'owns_leather_shoes', healOnly: true },
    { tag: 'bag', inkVar: 'owns_bag', healOnly: true },
    { tag: 'hair_bow', inkVar: 'owns_hair_bow', healOnly: true },
    { tag: 'cat_ears_headband', inkVar: 'owns_cat_ears_headband', healOnly: true },
    { tag: 'wig', inkVar: 'owns_wig', healOnly: true },
    { tag: 'crop_top', inkVar: 'owns_crop_top', healOnly: true },
    { tag: 'blouse', inkVar: 'owns_blouse', healOnly: true },
    { tag: 'sundress', inkVar: 'owns_sundress', healOnly: true },
    { tag: 'dress', inkVar: 'owns_dress', healOnly: true },
    { tag: 'oversized_sweater', inkVar: 'owns_oversized_sweater', healOnly: true },
    { tag: 'crop_jacket', inkVar: 'owns_crop_jacket', healOnly: true },
    { tag: 'nightie', inkVar: 'owns_nightie', healOnly: true },
    { tag: 'schoolgirl_uniform', inkVar: 'owns_schoolgirl_uniform', healOnly: true },
    { tag: 'maid_outfit', inkVar: 'owns_maid_outfit', healOnly: true },
    { tag: 'bunny_suit', inkVar: 'owns_bunny_suit', healOnly: true },
    { tag: 'skater_skirt', inkVar: 'owns_skater_skirt', healOnly: true },
    { tag: 'leggings', inkVar: 'owns_leggings', healOnly: true },
    { tag: 'miniskirt', inkVar: 'owns_miniskirt', healOnly: true },
    { tag: 'pleated_skirt', inkVar: 'owns_pleated_skirt', healOnly: true },
    { tag: 'sports_bra', inkVar: 'owns_sports_bra', healOnly: true },
    { tag: 'ribbon_bra', inkVar: 'owns_ribbon_bra', healOnly: true },
    { tag: 'padded_bra', inkVar: 'owns_padded_bra', healOnly: true },
    { tag: 'pushup_bra', inkVar: 'owns_pushup_bra', healOnly: true },
    { tag: 'lingerie_set', inkVar: 'owns_lingerie_set', healOnly: true },
    { tag: 'corset', inkVar: 'owns_corset', healOnly: true },
    { tag: 'high_heels', inkVar: 'owns_high_heels', healOnly: true },
    { tag: 'ankle_bracelet', inkVar: 'owns_ankle_bracelet', healOnly: true },
    { tag: 'choker', inkVar: 'owns_choker', healOnly: true },
    { tag: 'garter_belt', inkVar: 'owns_garter_belt', healOnly: true },
    { tag: 'purse', inkVar: 'owns_purse', healOnly: true },
    { tag: 'thigh_highs', inkVar: 'owns_thigh_highs', healOnly: true },
    { tag: 'knee_high_socks', inkVar: 'owns_knee_high_socks', healOnly: true },
    { tag: 'fishnet_stockings', inkVar: 'owns_fishnet_stockings', healOnly: true },
    { tag: 'garter_stockings', inkVar: 'owns_garter_stockings', healOnly: true },
    { tag: 'striped_thigh_highs', inkVar: 'owns_striped_thigh_highs', healOnly: true },
];

// Set bonus definitions (mirrors SetBonusData.cs)
const SET_BONUSES = [
    {
        name: 'Full Lingerie',
        requiredTags: ['lingerie_set', 'panties_black', 'garter_stockings'],
        bonuses: [{ stat: 'Allure', value: 3 }, { stat: 'Arousal', value: 2 }],
        inkFlag: 'set_full_lingerie',
    },
    {
        name: 'Maid Service',
        requiredTags: ['maid_outfit', 'high_heels', 'choker'],
        bonuses: [{ stat: 'Allure', value: 4 }, { stat: 'Submissiveness', value: 1 }],
        inkFlag: 'set_maid_service',
    },
    {
        name: 'Neko',
        requiredTags: ['cat_ears_headband', 'knee_high_socks', ['collar', 'masters_collar']],
        bonuses: [{ stat: 'Allure', value: 3 }, { stat: 'Comfort', value: 1 }],
        inkFlag: 'set_neko',
    },
    {
        name: 'Office Ready',
        requiredTags: ['blouse', 'skater_skirt', 'high_heels'],
        bonuses: [{ stat: 'Allure', value: 2 }, { stat: 'Comfort', value: 1 }],
        inkFlag: 'set_office_ready',
    },
    {
        name: 'Bunny Girl',
        requiredTags: ['bunny_suit', 'platform_heels', 'garter_stockings'],
        bonuses: [{ stat: 'Allure', value: 5 }, { stat: 'Arousal', value: 2 }],
        inkFlag: 'set_bunny_girl',
    },
    {
        name: 'Cozy Femme',
        requiredTags: ['oversized_sweater', 'leggings', 'knee_high_socks'],
        bonuses: [{ stat: 'Comfort', value: 3 }, { stat: 'Femininity', value: 1 }],
        inkFlag: 'set_cozy_femme',
    },
    {
        name: 'Dark Doll',
        requiredTags: ['ribbon_bra', 'striped_thigh_highs', 'pleated_skirt'],
        bonuses: [{ stat: 'Allure', value: 3 }, { stat: 'Femininity', value: 1 }],
        inkFlag: 'set_dark_doll',
    },
    {
        name: 'Pet Play',
        requiredTags: ['leather_collar_studded', 'leash_chrome', 'tail_plug_kitten'],
        bonuses: [{ stat: 'Allure', value: 3 }, { stat: 'Submissiveness', value: 1 }],
        inkFlag: 'set_pet_play',
    },
    {
        // KENNEL_EXPANSION.md §8 Phase-3. SetBonus_Puppy (SetBonus.ink) shipped
        // dormant and activates the moment this entry starts setting set_puppy.
        name: 'Good Puppy',
        requiredTags: ['puppy_hood', 'paw_mitts', 'tail_plug_puppy'],
        bonuses: [{ stat: 'Allure', value: 3 }, { stat: 'Submissiveness', value: 1 }],
        inkFlag: 'set_puppy',
    },
    {
        name: 'Latex Doll',
        requiredTags: ['latex_dress', 'latex_gloves', 'latex_stockings'],
        bonuses: [{ stat: 'Allure', value: 4 }, { stat: 'Femininity', value: 1 }, { stat: 'Comfort', value: -1 }],
        inkFlag: 'set_latex_doll',
    },
    {
        name: 'Training Set',
        requiredTags: [
            ['waist_trainer_starter', 'waist_trainer_advanced', 'waist_trainer_expert'],
            ['nipple_clamps_light', 'nipple_clamps_padded', 'nipple_clamps_heavy'],
            ['wrist_cuffs_pink', 'wrist_cuffs_locking', 'wrist_cuffs_heavy'],
        ],
        bonuses: [{ stat: 'Comfort', value: 2 }],
        inkFlag: 'set_training_active',
    },
    {
        name: 'Full Discipline',
        requiredTags: [
            ['waist_trainer_starter', 'waist_trainer_advanced', 'waist_trainer_expert'],
            ['nipple_clamps_light', 'nipple_clamps_padded', 'nipple_clamps_heavy'],
            ['wrist_cuffs_pink', 'wrist_cuffs_locking', 'wrist_cuffs_heavy'],
            ['heels_kitten', 'heels_stiletto', 'heels_platform_locking'],
        ],
        bonuses: [{ stat: 'Submissiveness', value: 1 }],
        inkFlag: 'set_full_discipline_active',
    },
    {
        // 0.3 intimate piercings (INTIMATE_PIERCINGS.md §4): all three back-room
        // piercings equipped. "Any 2 of 3" is not expressible in this checker
        // (requiredTags = every entry required), so the set demands full commitment.
        name: 'Private Commitment',
        requiredTags: ['piercing_nipple', 'piercing_cage_attached', 'piercing_thigh'],
        // 0.3 balance (2026-07-09): Arousal 3->1. Wearing all three piercings is
        // already +5 always-on Arousal (nipple+2, cage+2, thigh+1); a +3 set bonus
        // on top pushed a permanent +8 floor that fights the 0.2.9 arousal-spiral
        // rebalance (taper@85 + energy penalty at 75+). +1 keeps the set desirable
        // without parking the player near the penalty thresholds.
        bonuses: [{ stat: 'Allure', value: 2 }, { stat: 'Arousal', value: 1 }],
        inkFlag: 'set_private_commitment',
    },
];

// Training-gear tag groups for EquippedX flag computation.
// Mirrors EQUIPPED_FLAGS.md pattern — JS-computed only, NEVER list these
// in items.json `flags` (race footgun, see v0.2.3.5 panties bug).
const WAIST_TRAINER_TAGS = new Set(['waist_trainer_starter', 'waist_trainer_advanced', 'waist_trainer_expert']);
const NIPPLE_CLAMPS_TAGS = new Set(['nipple_clamps_light', 'nipple_clamps_padded', 'nipple_clamps_heavy']);
const WRIST_CUFFS_TAGS = new Set(['wrist_cuffs_pink', 'wrist_cuffs_locking', 'wrist_cuffs_heavy', 'wrist_cuffs_leather']);
const HEELS_TRAINING_TAGS = new Set(['heels_kitten', 'heels_stiletto', 'heels_platform_locking']);
// Regular Feet-slot heel shoes — NOT comfort-training items, but they DO count
// as "wearing heels" for narrative gates like the Public Challenge heels walk
// (PublicChallenges PC_Task3). Kept separate from HEELS_TRAINING_TAGS so the
// comfort-training "Practice walking in heels" entry stays scoped to the
// tolerance items. foundby: heels-walk challenge report 0.2.8.0.1.
const HEEL_SHOE_TAGS = new Set(['high_heels', 'platform_heels']);
// Restraint / latex acclimation line (0.2.9). One shared tolerance line for
// fetish gear that had no trained line. EquippedRestraint gates the
// "Acclimate to the gear" apartment training entry; the per-item
// restraint_tolerance buffs come from items.json (toleranceVar). Spans many
// slots (Head/Upper/Lower/Legwear/Accessory/Bra/Back) — this set is ONLY for
// the training flag, it does not affect any other slot/flag logic.
const RESTRAINT_TAGS = new Set([
    'latex_dress', 'latex_hood', 'latex_stockings', 'latex_gloves',
    'ball_gag', 'posture_collar', 'leather_collar_studded', 'leash_chrome',
    'thigh_harness', 'blindfold_satin', 'pvc_skirt', 'fishnet_bodysuit',
    'breast_forms', 'vibrating_plug',
]);

class InventoryManager {
    constructor() {
        /** @type {Map<string, object>} tag -> item definition */
        this._itemDb = new Map();
        /** @type {Set<string>} owned item tags */
        this._owned = new Set();
        /** @type {Map<string, string>} slot name -> equipped item tag */
        this._equipped = new Map();
        /** @type {Map<string, number>} stat name -> total buff value */
        this._activeBuffs = new Map();
        /** @type {Array<object>} currently active set bonuses */
        this._activeSets = [];
        /** @type {boolean} whether item DB has been loaded */
        this._dbLoaded = false;
        /** @type {function|null} callback to push variables to Ink */
        this._inkPusher = null;
        /** @type {function|null} callback to read a variable from Ink (varName → value) */
        this._inkReader = null;
        /** @type {object|null} saved outfit snapshot for save/restore */
        this._savedOutfit = null;

        // Listen for item tags from Ink (e.g. #item:add_panties_pink)
        events.on(Events.TAG_ITEM, (action, itemTag) => {
            if (action === 'add') {
                this.addItemByTag(itemTag);
            } else if (action === 'remove') {
                this.removeItemByTag(itemTag);
            } else if (action === 'equip') {
                this.equipByTag(itemTag);
            } else if (action === 'unequip') {
                // Story-forced removal (#item:unequip_<slot|tag>). value is either
                // a slot name (Front/Lower/...) or an item tag. Uses the RAW
                // unequip path — deliberately bypasses the wardrobe keyholder-cage
                // gate (isCageKeyholderLocked), so story beats (Chastity_Devotion,
                // Earned Relief, Quiet Exit, Walk-Away, the moved-out ending's
                // unequip_front) can take the cage off when the narrative says so.
                // Pre-0.2.8 this branch was MISSING → unequip_front / unequip_Lower
                // were silent no-ops in shipped content (Endgame.ink, Bar.ink).
                const SLOT_NAMES = ['Head', 'Upper', 'Lower', 'Bra', 'Underwear', 'Feet', 'Accessory', 'Item', 'Front', 'Back', 'Legwear', 'Piercing'];
                const slot = SLOT_NAMES.find(s => s.toLowerCase() === String(itemTag).toLowerCase());
                if (slot) {
                    this.unequipSlot(slot);
                } else {
                    this.unequipByTag(itemTag);
                }
            } else if (action === 'save_outfit') {
                this.saveOutfit();
            } else if (action === 'restore_outfit') {
                this.restoreOutfit();
            } else if (action === 'sync_flags') {
                // Force flag recompute — recovers desynced EquippedFeminine / wearing_* on load
                this._recalculateAllFlags();
            } else if (action === 'recalc_buffs') {
                // Re-pick buff arrays after a training-gear tolerance VAR
                // flips (training scene end). _pickBuffsByTolerance reads
                // the new tolerance value from Ink, swaps to buffsPartial /
                // buffsMastered, and pushes the new totals via _pushBuffsToInk.
                this.recalculateAllBuffs();
            } else if (action === 'retire_masc') {
                // Overnight beat (Fem past masc-clothing cap): take off any
                // masc top/bottom and re-dress feminine.
                this.retireMascClothes();
            } else if (action === 'destroy_front') {
                // Story-destroyed Front device (#item:destroy_front — the
                // locksmith cutting a welded cage into "two dead halves"):
                // unequip AND remove ownership so the wardrobe doesn't keep
                // listing a cage the narration destroyed. removeItemByTag's
                // _syncOwnershipVarsToInk recomputes owns_chastity from the
                // remaining owned set — correct even when the player owns a
                // second cage from the downsize chain. No Ink-side
                // owns_chastity write needed (audit 2026-06-11 finding 1:
                // those got clobbered back by the next purchase's recompute).
                const frontTag = this._equipped.get('Front');
                if (frontTag) {
                    this.unequipSlot('Front');
                    this.removeItemByTag(frontTag);
                    this.recalculateAllBuffs();
                }
            }
        });
    }

    /**
     * Load item definitions from JSON.
     * Call once at startup before any inventory operations.
     * @param {string} [url] path to items.json
     */
    async loadItemDatabase(url = 'assets/data/items.json?v=' + Date.now()) {
        // Idempotent fast-path: only short-circuit if a previous attempt
        // ACTUALLY populated the DB. Pre-fix this was set to `true` on
        // error too, which silently locked the session into an empty
        // wardrobe — single transient fetch fail (mobile-Safari hand-off,
        // content blocker, network blip during boot) → 4 days of gameplay
        // with `[Inventory] X not found` logs and no clothing UI. Player
        // report 2026-05-06, iOS 18.7 Safari, Free 0.2.5.8.
        if (this._dbLoaded && this._itemDb.size > 0) return;

        const MAX_ATTEMPTS = 3;
        let lastErr = null;
        for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
            try {
                // Attempt 1 keeps the ?v= cache-buster (fresh JSON); retries DROP
                // it — some content blockers / NSFW edge proxies 404 the timestamp
                // query, and reusing it 3x leaves the wardrobe permanently empty.
                // Mirrors fetchStoryJson / fetchWithEdgeRetry. foundby iPhone Safari
                // 0.2.9.0.4 Free ("wardrobe empty after ...", item DB 404'd all 3
                // retries on the rejected ?v= URL).
                const attemptUrl = attempt === 1 ? url : url.split('?')[0];
                const resp = await fetch(attemptUrl, { cache: attempt === 1 ? 'default' : 'reload' });
                if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
                const items = await resp.json();
                if (!Array.isArray(items) || items.length === 0) {
                    throw new Error(`empty or malformed items.json (length=${items?.length})`);
                }
                for (const item of items) {
                    this._itemDb.set(item.tag, Object.freeze(item));
                }
                this._dbLoaded = true;
                // Pre-cache all possible item flags
                this._allItemFlags = new Set();
                for (const item of this._itemDb.values()) {
                    if (item.flags) for (const flag of item.flags) this._allItemFlags.add(flag);
                }
                console.log(`[Inventory] Loaded ${this._itemDb.size} item definitions${attempt > 1 ? ` (attempt ${attempt})` : ''}`);
                return;
            } catch (e) {
                lastErr = e;
                console.warn(`[Inventory] item DB load attempt ${attempt}/${MAX_ATTEMPTS} failed: ${e.message}`);
                if (attempt < MAX_ATTEMPTS) {
                    // Linear backoff: 300ms, 600ms. Total worst case ~900ms over 3 attempts.
                    await new Promise(r => setTimeout(r, 300 * attempt));
                }
            }
        }
        // All attempts failed. DO NOT set _dbLoaded — leaves the door open
        // for inventory.js call sites (or a manual retry) to try again later.
        console.error('[Inventory] Failed to load item database after retries:', lastErr);
        events.emit('inventory:dbError', lastErr?.message || 'unknown');
    }

    /**
     * Defensive recovery: synchronous-feeling guard for callsites that
     * MUST have the DB. If the DB is empty, kick off a re-fetch in the
     * background. Returns true if the DB is populated, false otherwise.
     * Does NOT block — callers should still tolerate an empty DB and
     * log warnings as before.
     */
    ensureItemDatabaseLoaded() {
        if (this._itemDb.size > 0) return true;
        // Schedule a retry without blocking. Next inventory operation
        // (after the retry resolves) will see a populated DB.
        this.loadItemDatabase().catch(() => {});
        return false;
    }

    /**
     * Register a callback that pushes variable/flag changes to Ink.
     * Signature: (varName, value) => void
     */
    registerInkPusher(fn) {
        this._inkPusher = fn;
    }

    /**
     * Register a callback that reads a variable from Ink.
     * Used for cross-reference flags like has_visible_breasts which depend
     * on story progression (spa_breast_treatment) as well as equipment.
     * Signature: (varName) => value
     */
    registerInkReader(fn) {
        this._inkReader = fn;
    }


    // ---- Item DB access ----

    getItemByTag(tag) {
        return this._itemDb.get(tag) ?? null;
    }

    getAllItems() {
        return Array.from(this._itemDb.values());
    }

    /**
     * Get all owned item objects (for wardrobe UI).
     * @returns {Array<object>}
     */
    getOwnedItemObjects() {
        return Array.from(this._owned).map(tag => this._itemDb.get(tag)).filter(Boolean);
    }

    /**
     * Get equipped items as a Map<slotName, itemObject|null> (for wardrobe UI).
     * @returns {Map<string, object|null>}
     */
    getEquippedMap() {
        const map = new Map();
        for (const [slot, tag] of this._equipped) {
            map.set(slot, tag ? (this._itemDb.get(tag) ?? null) : null);
        }
        return map;
    }

    // ---- Ownership ----

    getOwnedItems() {
        return Array.from(this._owned).map(tag => this._itemDb.get(tag)).filter(Boolean);
    }

    getOwnedTags() {
        return Array.from(this._owned);
    }

    ownsItem(tag) {
        return this._owned.has(tag);
    }

    addItemByTag(tag) {
        const item = this._itemDb.get(tag);
        if (!item) {
            console.warn(`[Inventory] Item not found: '${tag}'`);
            return;
        }
        if (this._owned.has(tag)) {
            return; // already owned
        }
        this._owned.add(tag);
        this._syncOwnershipVarsToInk();
        events.emit(Events.ITEM_ADDED, item);
        console.log(`[Inventory] Added: ${item.displayName}`);
    }

    removeItemByTag(tag) {
        if (!this._owned.has(tag)) return;
        // Unequip if equipped
        for (const [slot, equippedTag] of this._equipped) {
            if (equippedTag === tag) {
                this.unequipSlot(slot);
                break;
            }
        }
        this._owned.delete(tag);
        this._syncOwnershipVarsToInk();
    }

    // ---- Equipment ----

    getEquippedItems() {
        const result = {};
        for (const [slot, tag] of this._equipped) {
            result[slot] = this._itemDb.get(tag) ?? null;
        }
        return result;
    }

    getEquippedItem(slot) {
        const tag = this._equipped.get(slot);
        return tag ? (this._itemDb.get(tag) ?? null) : null;
    }

    getEquippedTag(slot) {
        return this._equipped.get(slot) ?? null;
    }

    /**
     * Equip an item by tag. Must be owned.
     * Previous item in the same slot is automatically unequipped.
     */
    equipByTag(tag) {
        const item = this._itemDb.get(tag);
        if (!item) {
            console.warn(`[Inventory] Cannot equip unknown item: '${tag}'`);
            return;
        }
        if (!this._owned.has(tag)) {
            console.warn(`[Inventory] Cannot equip '${tag}' - not owned`);
            return;
        }

        // Multi-slot routing (Accessories, Piercings)
        let targetSlot = item.slot;
        const multiSlots = MULTI_SLOT_MAP[item.slot];
        if (multiSlots) {
            // Already equipped in some slot of this group? Still re-sync flags so callers
            // like Ink `#item:equip` can force a consistency check on desynced saves.
            for (const s of multiSlots) {
                if (this._equipped.get(s) === tag) {
                    this._recalculateAllFlags();
                    return;
                }
            }
            // Find first empty slot in group
            const empty = multiSlots.find(s => !this._equipped.has(s));
            // All full → replace last slot
            if (!empty) {
                const lastSlot = multiSlots[multiSlots.length - 1];
                const previousTag = this._equipped.get(lastSlot);
                if (previousTag) {
                    const previousItem = this._itemDb.get(previousTag);
                    if (previousItem) events.emit(Events.ITEM_UNEQUIPPED, previousItem, lastSlot);
                }
                targetSlot = lastSlot;
            } else {
                targetSlot = empty;
            }
        } else {
            const currentTag = this._equipped.get(targetSlot);
            if (currentTag === tag) {
                // Already equipped — still re-sync flags to recover from save desync.
                this._recalculateAllFlags();
                return;
            }
        }

        this._equipped.set(targetSlot, tag);
        this.recalculateAllBuffs();
        this._recalculateAllFlags();
        events.emit(Events.ITEM_EQUIPPED, item, targetSlot);
        console.log(`[Inventory] Equipped: ${item.displayName} -> ${targetSlot}`);
    }

    /**
     * Unequip item from a slot.
     */
    unequipSlot(slot) {
        const tag = this._equipped.get(slot);
        if (!tag) return;
        const item = this._itemDb.get(tag);
        this._equipped.delete(slot);
        this.recalculateAllBuffs();
        this._recalculateAllFlags();
        if (item) {
            events.emit(Events.ITEM_UNEQUIPPED, item, slot);
            console.log(`[Inventory] Unequipped: ${item.displayName}`);
        }
    }

    /**
     * Unequip item by tag (finds its slot).
     */
    unequipByTag(tag) {
        for (const [slot, equippedTag] of this._equipped) {
            if (equippedTag === tag) {
                this.unequipSlot(slot);
                return;
            }
        }
    }

    /**
     * Debug helper: add item and equip it immediately.
     */
    forceAddAndEquip(tag) {
        this.addItemByTag(tag);
        this.equipByTag(tag);
    }

    /**
     * Save current equipped items as a snapshot (for temporary outfit changes like bar work).
     */
    saveOutfit() {
        if (this._savedOutfit) {
            console.log('[Inventory] saveOutfit skipped — snapshot already exists (will be cleared by restoreOutfit)');
            return;
        }
        this._savedOutfit = {};
        for (const [slot, tag] of this._equipped) {
            this._savedOutfit[slot] = tag;
        }
        console.log(`[Inventory] Outfit saved (${Object.keys(this._savedOutfit).length} slots)`);
    }

    /**
     * Restore previously saved outfit snapshot.
     */
    restoreOutfit() {
        if (!this._savedOutfit) {
            console.warn('[Inventory] restoreOutfit called but no outfit was saved');
            return;
        }

        // Unequip all current items
        const currentSlots = Array.from(this._equipped.keys());
        for (const slot of currentSlots) {
            this.unequipSlot(slot);
        }

        // Re-equip saved items
        for (const [slot, tag] of Object.entries(this._savedOutfit)) {
            if (tag && this._owned.has(tag) && this._itemDb.has(tag)) {
                this._equipped.set(slot, tag);
            }
        }

        this.recalculateAllBuffs();
        this._recalculateAllFlags();
        console.log(`[Inventory] Outfit restored (${Object.keys(this._savedOutfit).length} slots)`);
        this._savedOutfit = null;
    }

    /**
     * Buy an item: deduct money, add to owned.
     * @param {string} tag
     * @param {function} getMoney - () => current money
     * @param {function} setMoney - (newVal) => void
     * @param {boolean} [hasDiscount] - perk_shop_discount active
     * @returns {boolean} success
     */
    buyItem(tag, getMoney, setMoney, hasDiscount = false) {
        const item = this._itemDb.get(tag);
        if (!item) return false;
        if (this._owned.has(tag)) return false;

        let price = item.price;
        if (hasDiscount) price = price - Math.floor(price / 10);

        const money = getMoney();
        if (money < price) return false;

        setMoney(money - price);
        this._owned.add(tag);
        events.emit(Events.ITEM_ADDED, item);
        events.emit(Events.ITEM_PURCHASED, item);
        console.log(`[Inventory] Bought: ${item.displayName} for $${price}`);
        return true;
    }

    // ---- Starter items ----

    async giveStarterItems() {
        this._owned.clear();
        this._equipped.clear();
        this._activeBuffs.clear();
        this._activeSets = [];

        // Defensive: starter items are critical for character viability —
        // an empty DB at this point means the boot-time loader hit a
        // transient failure (iOS Safari mobile network blip, content
        // blocker, etc.). Try one synchronous re-fetch before falling
        // through to the warning path. Foundby:VPS player 2026-05-06,
        // iOS 18.7 Safari, Free 0.2.5.8 — 4 days of "Starter item not
        // found" because the original fetch failed once and was never
        // retried.
        if (this._itemDb.size === 0) {
            console.warn('[Inventory] Item DB empty at giveStarterItems — attempting recovery fetch');
            await this.loadItemDatabase();
        }

        for (const tag of STARTER_ITEM_TAGS) {
            const item = this._itemDb.get(tag);
            if (item) {
                this._owned.add(tag);
                this._equipped.set(item.slot, tag);
            } else {
                console.warn(`[Inventory] Starter item not found: ${tag}`);
            }
        }
        this.recalculateAllBuffs();
        this._recalculateAllFlags();
        console.log(`[Inventory] Gave ${STARTER_ITEM_TAGS.length} starter items`);
    }

    // ---- Buff calculation (idempotent) ----

    /**
     * Recalculate all equipment buffs + set bonuses from scratch.
     * This is the ONLY way buffs are updated — never incremental.
     */
    recalculateAllBuffs({ suppressActivation = false } = {}) {
        // Snapshot previously-active set flags so we can detect new activations.
        // Story-trigger fires once per set the first time it becomes complete
        // (Studio Review Task #12). The "seen once" flag (set_<name>_seen)
        // is gated on the Ink side so the trigger doesn't re-fire after the
        // mirror beat has already played.
        //
        // v0.2.7 audit M6: callers reloading from save (restoreFromSave) pass
        // suppressActivation=true so the queue isn't flooded with no-op set
        // entries — the seen-flags are already in the loaded save and the
        // Ink dispatcher would just hit `-> END` for each one (leaving the
        // forced-event flag set if it ever got to drain).
        const previouslyActive = new Set(this._activeSets.map(s => s.inkFlag));

        this._activeBuffs.clear();
        this._activeSets = [];

        // Sum all equipped item buffs (tolerance-aware: training gear with
        // toleranceVar + buffsPartial/buffsMastered swaps to the trained
        // buff array as the player's body adapts — see COMFORT_TRAINING.md)
        for (const [_slot, tag] of this._equipped) {
            const item = this._itemDb.get(tag);
            if (!item || !item.buffs) continue;
            const buffArray = this._pickBuffsByTolerance(item);
            for (const buff of buffArray) {
                const cur = this._activeBuffs.get(buff.stat) || 0;
                this._activeBuffs.set(buff.stat, cur + buff.value);
            }
        }

        // Check set bonuses
        const equippedTags = new Set(this._equipped.values());
        for (const set of SET_BONUSES) {
            const allPresent = set.requiredTags.every(tag =>
                Array.isArray(tag) ? tag.some(t => equippedTags.has(t)) : equippedTags.has(tag)
            );
            if (allPresent) {
                this._activeSets.push(set);
                for (const bonus of set.bonuses) {
                    const cur = this._activeBuffs.get(bonus.stat) || 0;
                    this._activeBuffs.set(bonus.stat, cur + bonus.value);
                }
            }
        }

        // Push buffs via ink pusher (if registered)
        this._pushBuffsToInk();

        // Push set flags
        this._pushSetFlagsToInk();

        // Update all wearing/dressed/feminine flags
        this._recalculateAllFlags();

        events.emit(Events.BUFFS_RECALCULATED, Object.fromEntries(this._activeBuffs));

        // Fire once per newly-active set (the SetBonus.ink dispatcher gates
        // on set_<name>_seen so a re-equip after the mirror beat is silent).
        // M6: suppressActivation flag short-circuits the loop during save load.
        if (!suppressActivation) {
            for (const set of this._activeSets) {
                if (!previouslyActive.has(set.inkFlag)) {
                    events.emit(Events.SET_BONUS_ACTIVATED, { name: set.name, inkFlag: set.inkFlag });
                }
            }
        }
    }

    /**
     * Get the total buff value for a stat.
     */
    getBuff(stat) {
        return this._activeBuffs.get(stat) || 0;
    }

    /**
     * Get all active buffs as a plain object.
     */
    getAllBuffs() {
        return Object.fromEntries(this._activeBuffs);
    }

    /**
     * Get computed allure (equipment-only stat).
     * Used by Ink external get_allure().
     */
    getAllure() {
        return this._activeBuffs.get('Allure') || 0;
    }

    /**
     * Get computed comfort (equipment-only stat).
     * Used by Ink external get_comfort().
     */
    getComfort() {
        return this._activeBuffs.get('Comfort') || 0;
    }

    /**
     * Get active set bonuses.
     */
    getActiveSets() {
        return [...this._activeSets];
    }

    hasAnyActiveSetBonus() {
        return this._activeSets.length > 0;
    }

    /**
     * True if `tag` is masculine clothing (top OR bottom) the player can no
     * longer equip because Femininity has passed MASC_CLOTHING_FEM_CAP. The
     * wardrobe UI uses this to disable the equip button (with a reason); the
     * wardrobe:equip handler re-checks it to block the preset-apply bypass. NOT
     * enforced inside equipByTag — story-forced equips (#item:equip) must succeed.
     * @param {string} tag
     * @returns {boolean}
     */
    isMascClothingFemLocked(tag) {
        // masc_clothing_lock_enabled kill-switch stripped 2026-07-07 (armed 0.2.8).
        // The _masc visual image axis (appearance.js) is independent of this lock.
        if (!MASCULINE_UPPER_TAGS.has(tag) && !MASCULINE_LOWER_TAGS.has(tag)) return false;
        // MASC_ROUTE (0.2.8) §3: graduate the lock ROUTE-AWARE. A player committed
        // to the masculine route (masc_path >= 1, kill-switch armed) KEEPS the
        // right to present masculine above the Fem cap — the route inverts the
        // lock (lets him stay masculine instead of forcing feminine). On
        // masc_path == 0 (legacy / default feminize) this is byte-identical.
        {
            const mascPath = Number(this._inkReader?.('masc_path') ?? 0) | 0;
            if (mascPath >= 1) return false;
        }
        const fem = Number(this._inkReader?.('Femininity') ?? 0) | 0;
        return fem > MASC_CLOTHING_FEM_CAP;
    }

    /**
     * True if `item` lives in the generic "Item" slot but does NOTHING when
     * equipped — no buffs, and its equipped state is read by nobody. These are
     * owned/consumable items (dildo, sissy journal, hypno USB, rope set,
     * mummification sleeve): buying them sets an `owns_*` flag that gates a story
     * ACTION, not a worn effect. The wardrobe uses this to suppress the Equip
     * button so the player isn't offered a no-op that would only evict a real
     * buff item from the shared single Item slot. Worn toys (vibrator / remote
     * egg → wearing_vib) and the cosmetic buff consumables
     * (lip_gloss/nail_polish/perfume/shaving_kit) return false and stay
     * equippable. Keep WORN_ITEM_SLOT_TAGS in sync with _updateInkFlags.
     * @param {object} item
     * @returns {boolean}
     */
    isOwnedOnlyItem(item) {
        if (!item || item.slot !== 'Item') return false;
        const buffs = Array.isArray(item.buffs) ? item.buffs : [];
        if (buffs.length > 0) return false;
        return !WORN_ITEM_SLOT_TAGS.has(item.tag);
    }

    /**
     * True if `tag` is a chastity cage that the player can no longer take off
     * via the wardrobe because someone else holds the key. The wardrobe UI uses
     * this to disable the unequip button (with a reason); the wardrobe:unequip
     * AND wardrobe:loadPreset handlers re-check it to block the preset-apply
     * bypass. NOT enforced inside unequipSlot/unequipByTag — story-forced
     * removals (#item:unequip via TAG_ITEM, Chastity_Devotion / Earned Relief /
     * Quiet Exit) must succeed.
     *
     * Locked when: permanent_lock (any route); a Master keyholder holds it
     * (master_order_chastity >= 1 = he has actually ORDERED chastity, Bar /
     * FetishStore set it 1→3); OR an Independent keyholder-NPC holds the spare
     * key (keyholder_npc is Vivienne/Mira/roommate/random — assigned via the
     * "Decide who holds the spare key" picker; exit is the Chastity_Keyholder_
     * Request unlock beat). A SELF-bought cage with no keyholder (no Master
     * order, keyholder_npc still default "master", not permanent) stays freely
     * removable on ANY route — releases via Quiet Exit / the wardrobe.
     *
     * Pre-0.2.8.1 this used master_intensity >= 1, which is true for EVERY
     * Master-route player straight out of the editor — so a self-bought cage
     * was wrongly keyholder-locked before the Master said a word, locking the
     * player into the Master route and the forced morning re-lock (foundby:VPS
     * 0.2.8.1, "Chastity always locked even when not supposed to be").
     * master_order_chastity >= 1 subsumes the old >= 3 worn-on-order check.
     * Keep in sync with Globals.ink cage_keyholder_locked() + the Downtown.ink
     * RedLightEntry locksmith-choice guard.
     * @param {string} tag
     * @returns {boolean}
     */
    isCageKeyholderLocked(tag) {
        if (!CAGE_TAGS.has(tag)) return false;
        if (this._inkReader?.('permanent_lock')) return true;
        // Keyholder relinquished the cage to the player (Owner-refuse walk-away,
        // Locksmith_Refuse_Dream) — self-controlled now, freely removable. Checked
        // AFTER permanent_lock (a welded cage can't be "released", only destroyed)
        // and BEFORE the master_order / foreign-NPC terms, mirroring the Ink gate
        // cage_keyholder_locked() in Globals.ink. Lets master_order_chastity stay 3
        // (history) without re-locking a re-equipped cage.
        if (this._inkReader?.('cage_keyholder_released')) return false;
        if (Number(this._inkReader?.('master_order_chastity') ?? 0) >= 1
            && this._inkReader?.('wearing_chastity')) return true;
        // Independent keyholder-NPC (Vivienne/Mira/roommate/random) gave the key
        // away — the wardrobe can't pop the cage; release via Keyholder_Request.
        // keyholder_npc != "master" only happens after the Independent picker
        // assigns a foreign NPC (default + Master route both use "master").
        const kh = this._inkReader?.('keyholder_npc');
        if (kh && kh !== 'master' && this._inkReader?.('wearing_chastity')) return true;
        return false;
    }

    /**
     * Is `tag` a Master-downsize cage tier (regular → small → flat)? Equipping
     * one of these is allowed to displace a keyholder-locked cage, because the
     * player stays caged — the lock only protects against going UNcaged. The
     * belt (chastity_belt_pink) returns false so belt-over-locked-cage stays
     * blocked (deep-review P1-9). Used by the wardrobe/shop equip gates in
     * main.js to unblock the Master-ordered downsize swap.
     * @param {string} tag
     * @returns {boolean}
     */
    isCageDownsizeTier(tag) {
        return DOWNSIZE_CAGE_TAGS.has(tag);
    }

    /**
     * Take off any masculine top/bottom and re-dress feminine. Called from the
     * overnight "you can no longer present masculine past Fem 50" beat
     * (#item:retire_masc_clothes). For each emptied slot, equips an owned
     * feminine alternative; grants a basic fallback (sundress / leggings) if the
     * player owns nothing wearable, so is_dressed stays true and they're never
     * trapped at home undressed. Items stay OWNED — the Fem>50 equip-gate is
     * what keeps them off, so they become wearable again if Fem later drops.
     * @returns {boolean} true if anything was removed
     */
    retireMascClothes() {
        const upperTag = this._equipped.get('Upper');
        const lowerTag = this._equipped.get('Lower');
        let removed = false;
        if (upperTag != null && MASCULINE_UPPER_TAGS.has(upperTag)) { this._equipped.delete('Upper'); removed = true; }
        if (lowerTag != null && MASCULINE_LOWER_TAGS.has(lowerTag)) { this._equipped.delete('Lower'); removed = true; }
        if (!removed) return false;

        // Re-dress so is_dressed stays true (else the leave-home gate traps them).
        if (!this._equipped.has('Upper')) {
            const femUpper = [...this._owned].find(t => FEMININE_UPPER_TAGS.has(t));
            const fallback = femUpper || 'sundress';
            this._owned.add(fallback);
            this._equipped.set('Upper', fallback);
        }
        const up = this._equipped.get('Upper');
        const upperIsFullBody = up != null && FULL_BODY_UPPER_TAGS.has(up);
        if (!upperIsFullBody && !this._equipped.has('Lower')) {
            const altLower = [...this._owned].find(t =>
                FEMININE_LOWER_TAGS.has(t) || t === 'leggings' || t === 'bar_pants');
            const fallback = altLower || 'leggings';
            this._owned.add(fallback);
            this._equipped.set('Lower', fallback);
        }

        this.recalculateAllBuffs();
        this._recalculateAllFlags();
        console.log('[Inventory] Retired masc clothes (Femininity past cap) → re-dressed feminine');
        return true;
    }

    // ---- Wearing flags ----

    _recalculateAllFlags() {
        this._updateEquippedFeminineFlag();
        this._updateWearingSuitFlag();
        this._updateWearingWorkwearFlag();
        this._updateWearingFlags();
        this._updateDressedFlag();
        this._updateEquippedTrainingFlags();
        this._syncOwnershipVarsToInk();
        this._pushItemFlagsToInk();
    }

    /**
     * Pick the right buff array based on training tolerance.
     * Items without toleranceVar always return base buffs (back-compat for
     * all non-training items). Tolerance VAR is read from Ink:
     *   0 = untrained → buffs (default)
     *   1 = partial   → buffsPartial
     *   2 = mastered  → buffsMastered
     * @param {object} item items.json entry
     * @returns {Array<{stat:string,value:number}>}
     */
    _pickBuffsByTolerance(item) {
        if (!item.toleranceVar) return item.buffs;
        if (!item.buffsPartial && !item.buffsMastered) return item.buffs;
        const tolerance = Number(this._inkReader?.(item.toleranceVar) ?? 0) | 0;
        if (tolerance >= 2 && item.buffsMastered) return item.buffsMastered;
        if (tolerance >= 1 && item.buffsPartial) return item.buffsPartial;
        return item.buffs;
    }

    // EquippedWaistTrainer / NippleClamps / Cuffs / Heels — JS-computed
    // flags read by Ink hub-choice guards (Apartment training entries).
    // Per EQUIPPED_FLAGS.md race rule: NEVER list these in items.json `flags`.
    _updateEquippedTrainingFlags() {
        const tags = new Set(this._equipped.values());
        let wt = false, nc = false, cu = false, he = false, heelsAny = false, re = false;
        for (const t of tags) {
            if (WAIST_TRAINER_TAGS.has(t)) wt = true;
            if (NIPPLE_CLAMPS_TAGS.has(t)) nc = true;
            if (WRIST_CUFFS_TAGS.has(t)) cu = true;
            if (HEELS_TRAINING_TAGS.has(t)) he = true;
            if (HEELS_TRAINING_TAGS.has(t) || HEEL_SHOE_TAGS.has(t)) heelsAny = true;
            if (RESTRAINT_TAGS.has(t)) re = true;
        }
        this._pushInk('EquippedWaistTrainer', wt);
        // Per-tier waist training (0.3.0.4): which trainer is worn — the
        // Advanced/Expert corsets carry their own break-in tracks. Highest
        // tier wins if multiple Accessory slots hold trainers (cage_tier pattern).
        let wtTier = 0;
        if (tags.has('waist_trainer_starter')) wtTier = 1;
        if (tags.has('waist_trainer_advanced')) wtTier = 2;
        if (tags.has('waist_trainer_expert')) wtTier = 3;
        this._pushInk('waist_trainer_tier_equipped', wtTier);
        this._pushInk('EquippedNippleClamps', nc);
        this._pushInk('EquippedCuffs', cu);
        this._pushInk('EquippedHeels', he);
        // Restraint / latex acclimation line (0.2.9): true if any RESTRAINT_TAGS
        // item is equipped. Gates the "Acclimate to the gear" training entry.
        this._pushInk('EquippedRestraint', re);
        // "Wearing any heels" — training heels OR regular Feet-slot heel shoes.
        // Used by narrative gates (heels-walk challenge); EquippedHeels stays
        // training-only for the comfort-training entry.
        this._pushInk('wearing_heels', heelsAny);
    }

    _updateEquippedFeminineFlag() {
        const upperTag = this._equipped.get('Upper');
        const braTag = this._equipped.get('Bra');
        const lowerTag = this._equipped.get('Lower');
        const isFeminineUpper = (upperTag != null && FEMININE_UPPER_TAGS.has(upperTag));
        const isFeminine = isFeminineUpper
            || (braTag != null && FEMININE_BRA_TAGS.has(braTag));
        this._pushInk('EquippedFeminine', isFeminine);
        const isFeminineLower = lowerTag != null && FEMININE_LOWER_TAGS.has(lowerTag);
        // wearing_masc_clothes: a masc top OR bottom is on. Drives the overnight
        // "retire boy clothes past Fem 50" beat (Sleep.ink overnight_processing).
        // Suit is intentionally EXCLUDED here — the office route must not get its
        // suit retired overnight.
        const wearingMasc = (upperTag != null && MASCULINE_UPPER_TAGS.has(upperTag))
            || (lowerTag != null && MASCULINE_LOWER_TAGS.has(lowerTag));
        this._pushInk('wearing_masc_clothes', wearingMasc);
        // presenting_masc drives the _masc image/video axis (appearance.js).
        // The outfit must ACTUALLY read masculine — a masc top/bottom OR the
        // office suit — AND carry NO feminine garment (top/bra/skirt). Mere
        // ABSENCE of feminine clothes (nude, sleepwear, neutral robe, undressed
        // morning mirror) no longer counts as masc: a feminized / long-haired PC
        // was rendering as a short-haired man on every hub view where they
        // weren't currently in a flagged-feminine outfit. foundby:VPS player
        // 0.2.8 ("sieht nicht mehr das Langhaar-Bild" while wearing a dress —
        // the apartment mirror fired pre-dressing with no feminine top on).
        // Only VISIBLE feminine outerwear (feminine top OR skirt) drops the masc
        // render — a hidden feminine bra/corset under a masc shirt no longer flips
        // it (EquippedFeminine above STILL counts the bra for narrative gates;
        // isFeminineUpper deliberately excludes braTag). 0.2.8.4.x, owner request.
        const presentingMasc = (wearingMasc || upperTag === 'suit' || upperTag === 'workwear')
            && !(isFeminineUpper || isFeminineLower);
        this._pushInk('presenting_masc', presentingMasc);
    }

    // Drives male office route fork (Office.ink Office_Interview).
    // Single-item flag — mirrors wearing_wig pattern, not FEMININE_UPPER set.
    _updateWearingSuitFlag() {
        const upperTag = this._equipped.get('Upper');
        this._pushInk('wearing_suit', upperTag === 'suit');
    }

    // Drives the Construction Site +5 pay bonus (ConstructionSite.ink) and the
    // _masc presentation axis. Mirrors _updateWearingSuitFlag: single-item flag,
    // NOT in MASCULINE_UPPER_TAGS, so the overnight masc-retire and the
    // Fem-cap clothing lock leave the job outfit alone (suit precedent).
    // Shipped 2026-06-10 (deep-review P1-16: the flag had zero setters and the
    // pay bonus was dead code).
    _updateWearingWorkwearFlag() {
        const upperTag = this._equipped.get('Upper');
        this._pushInk('wearing_workwear', upperTag === 'workwear');
    }

    _updateWearingFlags() {
        const frontTag = this._equipped.get('Front');
        // chastity_belt_pink (KITTEN_CHROME §4b) is a Front chastity device: it
        // sets wearing_chastity (so _chastity image variants + chastity gates
        // fire) but is NOT a cage_tier.
        this._pushInk('wearing_chastity',
            frontTag === 'chastity_cage' || frontTag === 'pink_cage' || frontTag === 'flat_cage'
            || frontTag === 'chastity_belt_pink');

        // cage_tier: 0=none, 1=regular, 2=small, 3=flat — consumed by Master
        // downsize chain (Bar.ink Master_Order_Downsize) and shop gating. The
        // belt stays tier 0 so the cage-downsize chain never mis-reads it.
        let cageTier = 0;
        if (frontTag === 'chastity_cage') cageTier = 1;
        else if (frontTag === 'pink_cage') cageTier = 2;
        else if (frontTag === 'flat_cage') cageTier = 3;
        this._pushInk('cage_tier', cageTier);

        // chastity_form: "cage" vs "belt" so downsize/narration can distinguish
        // the two Front devices even though both set wearing_chastity. "" = bare.
        let chastityForm = '';
        if (frontTag === 'chastity_belt_pink') chastityForm = 'belt';
        else if (cageTier > 0) chastityForm = 'cage';
        this._pushInk('chastity_form', chastityForm);

        const backTag = this._equipped.get('Back');
        this._pushInk('wearing_plug',
            backTag === 'plug' || backTag === 'plug_medium' ||
            backTag === 'plug_large' || backTag === 'vibrating_plug' ||
            backTag === 'tail_plug_kitten' || backTag === 'tail_plug_puppy');
        // Separate flag: training-gate uses this to skip vibrating plug
        // (stimulation tool, not training tool — see COMFORT_TRAINING.md §3.6.1)
        this._pushInk('wearing_vibrating_plug', backTag === 'vibrating_plug');
        // Pet tail: a plug WITH a visible tail. NPC reaction beats gate on
        // this exact flag (not Fem/Sub), match the real state. (KITTEN_CHROME_RESTOCK.md §4a)
        // OR-across-tags: kitten + puppy siblings both count (KENNEL_EXPANSION.md §8,
        // feedback_inventory_owns_or_pattern). Extend this set, never add a
        // second flag, when a new tailed plug ships.
        this._pushInk('wearing_tail', TAIL_PLUG_TAGS.has(backTag));

        const headTag = this._equipped.get('Head');
        this._pushInk('wearing_wig', headTag === 'wig');

        const itemTag = this._equipped.get('Item');
        this._pushInk('wearing_vib', itemTag === 'vibrator' || itemTag === 'remote_vib');

        // Free Use track (0.3.1): the chrome availability anklet is an Accessory
        // (multi-slot, auto-routed), so scan ALL equipped tags, not one slot.
        // wearing_freeuse_anklet gates every venue free-use choice — taking the
        // anklet off IS the in-fiction pause (FREE_USE.md §1). Ink-only flag,
        // owned/derived by inventory like wearing_tail. Both inkPusher sites
        // route booleans through setFlag, so this one line covers both paths.
        this._pushInk('wearing_freeuse_anklet',
            [...this._equipped.values()].includes('freeuse_anklet'));

        // Breast visibility: breast_forms equipment OR spa progression (dual trigger).
        // Image variants with `_boobs` suffix must also resolve for players who grew
        // breasts via the spa questline without ever equipping breast_forms.
        const braTag = this._equipped.get('Bra');
        const spaStage = this._inkReader?.('spa_breast_treatment') || 0;
        this._pushInk('has_visible_breasts', braTag === 'breast_forms' || spaStage >= 2);
    }

    _updateDressedFlag() {
        // "Dressed" means publicly presentable — not going outside topless
        // (pants only) or pantsless (shirt only). Fix 2026-04-23 after player
        // bug report: previously `hasUpper || hasLower`, so a player wearing
        // only jeans (no shirt) passed the is_dressed gate and walked outside
        // topless with no warning.
        const upperTag = this._equipped.get('Upper');
        const underwearTag = this._equipped.get('Underwear');
        // The "bottom half" is the Lower slot OR a swimwear bottom worn in the
        // Underwear slot (resort bikini = top in Upper + bottom in Underwear,
        // so Lower is legitimately empty). Plain underwear (boxers/panties) does
        // NOT count — walking out in just panties stays blocked. foundby:VPS
        // 2026-06-25 (bikini-clad player trapped at the leave-apartment gate).
        const hasBottomHalf = this._equipped.has('Lower')
            || (underwearTag != null && SWIMWEAR_BOTTOM_TAGS.has(underwearTag));
        const wearingFullBodyUpper = upperTag && FULL_BODY_UPPER_TAGS.has(upperTag);
        const hasBothHalves = !!upperTag && hasBottomHalf;
        this._pushInk('is_dressed', wearingFullBodyUpper || hasBothHalves);
    }

    _pushItemFlagsToInk() {
        // Collect all flags from equipped items
        const activeFlags = new Set();
        for (const [_slot, tag] of this._equipped) {
            const item = this._itemDb.get(tag);
            if (item?.flags) {
                for (const flag of item.flags) {
                    activeFlags.add(flag);
                }
            }
        }

        // Push: set active flags true, inactive false. Skip flags that are
        // computed explicitly elsewhere — otherwise an item's flags array
        // overrides _updateEquippedFeminineFlag's tag-set logic (and the
        // EquippedPanties computation below), and we get the v0.2.3.5 bug
        // where only cafe_maid_uniform (the single item that happened to
        // carry "EquippedFeminine" in its flags) satisfied the Office / Maid
        // / Club gates.
        const allFlags = this._allItemFlags || new Set();
        for (const flag of allFlags) {
            if (flag === 'EquippedFeminine' || flag === 'EquippedPanties') continue;
            this._pushInk(flag, activeFlags.has(flag));
        }

        // EquippedPanties: true if underwear is a feminine item
        const underwearTag = this._equipped.get('Underwear');
        const isPanties = underwearTag === 'panties_pink' || underwearTag === 'panties_black';
        this._pushInk('EquippedPanties', isPanties);
    }

    _syncOwnershipVarsToInk() {
        // Multiple tags can map to the same inkVar (e.g. chastity_cage / pink_cage /
        // flat_cage all → owns_chastity). OR across tags so pushing false on
        // remove doesn't clobber a sibling-tag's true.
        const ownedByVar = new Map();
        for (const { tag, inkVar } of OWNERSHIP_VAR_MAP) {
            const prior = ownedByVar.get(inkVar) || false;
            ownedByVar.set(inkVar, prior || this._owned.has(tag));
        }
        for (const [inkVar, owned] of ownedByVar) {
            this._pushInk(inkVar, owned);
        }
    }

    _pushBuffsToInk() {
        const buffableStats = [
            'Femininity', 'Submissiveness', 'Arousal',
            'Allure', 'Comfort', 'Energy', 'Money',
        ];
        for (const stat of buffableStats) {
            const val = this._activeBuffs.get(stat) || 0;
            // Push as buffed var (engine's variable manager handles base + buff)
            if (this._inkPusher) {
                this._inkPusher(stat, val);
            }
        }
    }

    _pushSetFlagsToInk() {
        const activeNames = new Set(this._activeSets.map(s => s.inkFlag));
        for (const set of SET_BONUSES) {
            this._pushInk(set.inkFlag, activeNames.has(set.inkFlag));
        }
    }

    _pushInk(name, value) {
        if (this._inkPusher) {
            this._inkPusher(name, value);
        }
    }

    // ---- Ink → Inventory sync (for save load) ----

    /**
     * Read all OWNERSHIP_VAR_MAP entries from the Ink story state and add
     * the corresponding item tags to _ownedItems if the Ink var is true.
     * This fixes the case where items were granted via Ink (e.g. owns_chastity = true)
     * but never went through addItemByTag(), so they're missing from the save's ownedTags.
     * Call AFTER Ink state is restored (story.state.LoadJson) and after restoreFromSave().
     * @param {object} story - the inkjs Story instance
     */
    syncFromInk(story) {
        if (!story?.variablesState) {
            console.warn('[Inventory] syncFromInk: no story or variablesState');
            return;
        }
        let added = 0;
        // Group by inkVar: multi-tag families (owns_chastity, owns_plug, owns_dildo)
        // only assert "at least one family member is owned". If any member is
        // already owned there is nothing to heal — granting the remaining tags
        // would gift items the player never bought (0.2.8 belt over-grant on
        // every load, DEEP_REVIEW P0-4). Heal-grant uses the first-declared
        // (base) tag only.
        const famByVar = new Map();
        for (const { tag, inkVar } of OWNERSHIP_VAR_MAP) {
            if (!famByVar.has(inkVar)) famByVar.set(inkVar, []);
            famByVar.get(inkVar).push(tag);
        }
        for (const [inkVar, tags] of famByVar) {
            const val = story.variablesState[inkVar];
            if (!val) continue;
            if (tags.some(t => this._owned.has(t))) continue;
            const canonical = tags[0];
            if (this._itemDb.has(canonical)) {
                this._owned.add(canonical);
                added++;
                console.log(`[Inventory] syncFromInk: added '${canonical}' (Ink '${inkVar}' = true)`);
            }
        }
        // Reverse sync: remove items from inventory that Ink says are NOT owned
        let removed = 0;
        for (const { tag, inkVar, healOnly } of OWNERSHIP_VAR_MAP) {
            // healOnly entries (regular clothing) never reverse-delete: a
            // pre-0.3.0.7 save has these vars defaulting false, and deleting on
            // false would wipe the entire wardrobe on first load. Clothing only
            // ever HEALS forward (add-if-true above). See OWNERSHIP_VAR_MAP.
            if (healOnly) continue;
            const val = story.variablesState[inkVar];
            if (!val && this._owned.has(tag)) {
                // Ink says not owned, but inventory says owned — repair inconsistency
                this._owned.delete(tag);
                // Unequip if currently equipped
                for (const [slot, equippedTag] of this._equipped) {
                    if (equippedTag === tag) {
                        this._equipped.delete(slot);
                        break;
                    }
                }
                removed++;
                console.log(`[Inventory] syncFromInk: removed '${tag}' (Ink '${inkVar}' = false)`);
            }
        }

        if (added > 0 || removed > 0) {
            this.recalculateAllBuffs();
            console.log(`[Inventory] syncFromInk: synced ${added} added, ${removed} removed from Ink state`);
        }
    }

    // ---- Save/Load ----

    /**
     * Get serializable inventory state for save system.
     */
    getSerializableState() {
        const equippedSlots = [];
        const equippedTags = [];
        for (const [slot, tag] of this._equipped) {
            equippedSlots.push(slot);
            equippedTags.push(tag);
        }
        return {
            ownedTags: Array.from(this._owned),
            equippedSlots,
            equippedTags,
            savedOutfit: this._savedOutfit || null,
        };
    }

    /**
     * Restore inventory from save data.
     */
    restoreFromSave(ownedTags, equippedSlots, equippedTags) {
        this._owned.clear();
        this._equipped.clear();
        this._activeBuffs.clear();
        this._activeSets = [];

        // Restore owned
        for (const tag of ownedTags) {
            if (this._itemDb.has(tag)) {
                this._owned.add(tag);
            } else {
                console.warn(`[Inventory] Unknown item tag in save: '${tag}'`);
            }
        }

        // Restore equipped
        const len = Math.min(equippedSlots.length, equippedTags.length);
        for (let i = 0; i < len; i++) {
            const slot = equippedSlots[i];
            const tag = equippedTags[i];
            if (this._itemDb.has(tag)) {
                // Save data should always have equipped items in ownedTags; if it's
                // missing, the save was written by a buggy path. Warn so telemetry
                // can spot the source instead of silently auto-healing.
                if (!this._owned.has(tag)) {
                    console.warn(`[Inventory] Save corruption: equipped tag '${tag}' missing from ownedTags — auto-adding`);
                    this._owned.add(tag);
                }
                this._equipped.set(slot, tag);
            }
        }

        // Migration (0.3.0.8): perfume / lip_gloss / nail_polish moved out of the
        // shared single Item slot into their own Cosmetic multi-slot. A pre-migration
        // save restores one of them under 'Item' (they were mutually exclusive, so at
        // most one). Re-route it to a Cosmetic slot so it shows under the Cosmetic tab.
        // Buffs recompute from equipped tags regardless of slot key, so no buff is lost.
        const _legacyItemTag = this._equipped.get('Item');
        if (_legacyItemTag) {
            const _def = this._itemDb.get(_legacyItemTag);
            if (_def && _def.slot === 'Cosmetic') {
                this._equipped.delete('Item');
                this._equipped.set('Cosmetic', _legacyItemTag);
            }
        }

        // v0.2.7 audit M6: skip activation events on load — the save already
        // carries set_<name>_seen flags, so re-firing them would just hit
        // `-> END` in SetBonus.ink with no narrative output AND no #return:done
        // (queue stays in-flight forever for that drain).
        this.recalculateAllBuffs({ suppressActivation: true });
        this._recalculateAllFlags();
        console.log(`[Inventory] Restored ${this._owned.size} owned, ${this._equipped.size} equipped`);
    }

    /**
     * Restore saved outfit snapshot from save data (called after restoreFromSave).
     */
    restoreSavedOutfit(savedOutfit) {
        this._savedOutfit = savedOutfit || null;
    }

    /**
     * Reset inventory to empty state.
     */
    reset() {
        this._owned.clear();
        this._equipped.clear();
        this._activeBuffs.clear();
        this._activeSets = [];
    }
}

export { SET_BONUSES, MASC_CLOTHING_FEM_CAP };
export const inventoryManager = new InventoryManager();
